import React from "react";
import { Box, Typography } from "@material-ui/core";

// Pipeline stage mapping matching backend PIPELINE_STAGE
export const PIPELINE_STAGE = {
  resources: 0,
  case_study: 0, // case study artifacts stored at resources stage (0) in backend
  outline: 1,
  slide_deck: 2,
  lecture_notes: 3,
  slide_deck_with_notes: 4,
};

// Display names for each stage (UI tab indices 0-5)
export const DISPLAY_STAGES = [
  "Resources",
  "Case Study",
  "Outline",
  "Slide Deck",
  "Lecture Notes",
  "Slide Deck with Notes",
];

// Status to step mapping - backend status values map to step indices
export const STATUS_TO_STEP_MAP = {
  // Initial state
  "Not Started": 0,
  "Resources Uploaded": 0,

  // Queue states
  "In Case Study Generation Queue": 0,
  "In Outline Generation Queue": 1,
  "In Slide Deck Generation Queue": 2,
  "In Lecture Notes Generation Queue": 3,
  "In Slide Deck with Notes Generation Queue": 4,

  // Completed states
  "Case Study Generated": 1,
  "Outline Generated": 2,
  "Slide Deck Generated": 3,
  "Lecture Notes Generated": 4,
  "Slide Deck with Notes Generated": 5,

  // Error state
  "Failed": -1,
};

// Loading messages for each queue state
export const QUEUE_LOADING_OPTIONS = {
  "In Case Study Generation Queue":
    "Generating case study from input materials...",
  "In Outline Generation Queue":
    "Parsing input files, instructions and generating outline...",
  "In Slide Deck Generation Queue":
    "Generating slide deck from outline...",
  "In Lecture Notes Generation Queue":
    "Generating lecture notes from slide deck...",
  "In Slide Deck with Notes Generation Queue":
    "Generating final slide deck with speaker notes...",
};

// Pipeline keys for each stage transition
export const PIPELINE_KEYS = {
  // Stage 0 -> Stage 1: Run pipeline 1 to generate case study
  resourcesCaseStudy: "1",
  // Stage 1 -> Stage 2: Run pipeline 3c to generate outline from case study
  caseStudyNext: "3c",
  // Stage 0 -> Stage 2: Skip case study, run pipeline 3a to generate outline directly
  resourcesNext: "3a",
  // Stage 2 -> Stage 3: Run pipeline 4 to generate slide deck
  outlineNext: "4",
  // Stage 3 -> Stage 4: Run pipeline 5 to generate lecture notes
  slideDeckNext: "5",
  // Stage 4 -> Stage 5: Run pipeline 6 to generate slide deck with notes
  lectureNotesNext: "6",
};

// Artifact types produced at each stage (UI tab index)
export const STAGE_ARTIFACT_TYPES = {
  0: [], // Resources stage - no artifacts, just inputs
  1: ["case_study_tex", "case_study_pdf"], // Case Study stage
  2: ["lecture_outline_md", "case_study_outline_md"], // Outline stage
  3: ["slide_deck_tex", "slide_deck_pdf"], // Slide deck stage
  4: ["lecture_notes_tex", "lecture_notes_pdf"], // Lecture notes stage
  5: ["slide_deck_with_notes_tex", "slide_deck_with_notes_pdf"], // Final stage
};

// Stage titles and subheaders (UI tab index)
export const STAGE_DESCRIPTIONS = {
  0: {
    title: "Project Resources",
    subheader: "Upload your source materials and provide instructions for the AI to follow.",
  },
  1: {
    title: "Case Study Generation",
    subheader: "Review the generated case study and configure instructions for outline generation.",
  },
  2: {
    title: "Outline Generation",
    subheader: "Review the generated outline and refine it before proceeding to slide generation.",
  },
  3: {
    title: "Slide Deck Design",
    subheader: "Edit the LaTeX source and preview your slides. Recompile to see changes.",
  },
  4: {
    title: "Lecture Notes",
    subheader: "Customize your lecture notes based on the generated slide content.",
  },
  5: {
    title: "Slide Deck with Speaker Notes",
    subheader: "Access your final slide deck integrated with speaker notes.",
  },
};

// Default instructions for each pipeline stage (UI tab index)
// Stage 0: instructions for case study generation
// Stage 1: instructions for outline generation (used in Case Study stage "Configure Next Step")
// Stage 2: instructions for slide deck generation (used in Outline stage "Configure Next Step")
// etc.
export const DEFAULT_INSTRUCTIONS = {
  0: `## Request: Generate Case Study

### Instructions
Create a comprehensive case study document that will serve as the foundation for slide generation.
The case study should provide rich context, real-world examples, and key data points.

**Case Study Details:**
- Subject/Company: [Name]
- Industry: [Industry]
- Topic Focus: [Primary problem or scenario]
- Target Audience: [Students / Practitioners / Executives]

**Key Learning Objectives:**
1. [LO1]
2. [LO2]
3. [LO3]

**Sections to Include:**
- Background and Context
- Problem Statement
- Data and Evidence
- Analysis
- Outcomes / Lessons Learned`,

  1: `## Request: Create Session Outline

### Instructions
Create a detailed session outline that will serve as the blueprint for slide generation.
The outline should reference the case study and map all content to specific slides.

**Session Details:**
- Session Number: [N]
- Session Title: [Title]
- Number of Slides: [X]
- Previous Session: [What was covered]
- Next Session: [What comes next]


**Learning Outcomes:**
1. [LO1]
2. [LO2]
3. [LO3]
4. [LO4]`,

  2: `## Request: Generate Slide Deck

### Instructions
Create a visually stunning Beamer slide deck.
Use web search to verify all statistics and find authoritative citations.
Use extended thinking to ensure comprehensive coverage.

### CRITICAL REQUIREMENTS
1. **Use Web Search** for every statistic - verify with authoritative sources
2. **Use Extended Thinking** to plan the visual design before coding
3. **Cross-Reference** all case study facts with the case study document
4. **No Unsourced Claims** - every number needs a citation`,

  3: `## Request: Generate Lecture Notes from Slide Deck

### Instructions
Create comprehensive lecture  notes that elaborate on every slide in the deck.
The notes should serve as the authoritative student reference.

### Content Requirements

1. **Complete Coverage:** Every slide topic must have a corresponding section
2. **Elaboration:** Expand bullet points into full explanations
3. **Definitions:** Formal definitions for all key terms
4. **Examples:** Concrete examples for abstract concepts
5. **Citations:** Verify and expand all slide citations
6. **Frameworks:** Full framework descriptions (not just names)

### Slide-to-Notes Mapping

For each slide in the deck:
1. Create a section or subsection
2. Expand the 3-5 bullet points into 2-3 paragraphs each
3. Add context not shown on slides
4. Include relevant quotes from source documents
5. Connect to case study where applicable`,

  4: `## Request: Generate Slide Deck with Speaker Notes

### Instructions
Create a comprehensive slide deck where each content slide is followed by a speaker notes slide.

### Integration Requirements

1. **Preserve All Content Slides:** Copy exactly from the original slide deck
2. **Add Speaker Notes Slides:** Insert after each content slide
3. **Draw from Lecture Notes:** Use detailed content for talking points
4. **Write Punchlines:** Create memorable one-liners for each slide
5. **Add Timing:** Estimate duration for each slide
6. **Set Energy:** Describe the tone for delivery
7. **Write Transitions:** Connect each slide to the next`,

  5: `# Final Review Instructions

## Overview
This is the final stage. Use these instructions to guide any last-minute refinements or regeneration.

## Review Checklist
- Content accuracy and completeness
- Visual consistency across all materials
- Proper formatting and styling
- Citation and reference verification

## Refinements
Note any specific changes or improvements needed for the final deliverables.

## Additional Notes
Document any lessons learned or preferences for future projects.`,
};

/**
 * Component to display the current stage title and subheader
 */
export const StageHeader = ({ activeTab }) => {
  const description = STAGE_DESCRIPTIONS[activeTab];
  if (!description) return null;

  return (
    <Box mt={3} mb={-2} ml={1}>
      <Typography variant="h5" style={{ fontWeight: 500, color: "#20677b" }}>
        {description.title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {description.subheader}
      </Typography>
    </Box>
  );
};

// Helper function to map status to step index
export const mapStatusToStep = (status) => {
  if (!status) return 0;
  const trimmedStatus = status.trim();
  return STATUS_TO_STEP_MAP[trimmedStatus] ?? 0;
};

// Helper function to check if status is a queue/loading state
export const isQueueStatus = (status) => {
  if (!status) return false;
  return QUEUE_LOADING_OPTIONS.hasOwnProperty(status.trim());
};

// Get the loading message for a queue status
export const getQueueLoadingMessage = (status) => {
  if (!status) return "Loading...";
  return QUEUE_LOADING_OPTIONS[status.trim()] || "Processing...";
};

// Get expected time message
export const getExpectedTimeMessage = () => {
  const min = 2 + Math.floor(Math.random() * 3);
  const max = 4 + Math.floor(Math.random() * 4);
  return `Expected time: ${min}-${max} minutes. You can leave this tab, processing will continue in the background.`;
};
